import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from './api';

export type Range = { start: string; end: string };
export type BlockReason = 'pause' | 'day_off' | 'sick' | 'vacation' | 'meeting' | 'other';
export type DisplayStatus = 'requested' | 'booked' | 'client_confirmed' | 'completed' | 'cancelled' | 'no_show';
export type PartKind = 'buffer_before' | 'active' | 'processing' | 'buffer_after';

export type CalendarAppointment = {
  id: string;
  reference: string;
  status: DisplayStatus;
  source: 'online' | 'app' | 'walk_in' | 'import';
  startsAt: string;
  endsAt: string;
  version: number;
  customerName: string | null;
  alert: boolean;
  newClient: boolean;
  requested: boolean;
  note: string | null;
  staffIds: string[];
  canEdit: boolean;
  services: string[];
  priceCents: number;
  parts: Array<{ staffMemberId: string; kind: PartKind; start: string; end: string }>;
};

export type CalendarBlock = {
  blockId: string;
  staffMemberId: string | null;
  start: string;
  end: string;
  date: string;
  reason: BlockReason;
  note: string | null;
  allDay: boolean;
  repeats: boolean;
};

export type CalendarData = {
  timeZone: string;
  from: string;
  to: string;
  open: Range[];
  closedDays: Array<{ date: string; holiday: string | null; holidayName: { nl: string; en: string } | null; message: string | null }>;
  staff: Array<{ id: string; displayName: string; color: string; scheduled: Range[] }>;
  blocks: CalendarBlock[];
  appointments: CalendarAppointment[];
};

export type AppointmentDetail = {
  id: string;
  reference: string;
  locationId: string;
  status: DisplayStatus;
  source: CalendarAppointment['source'];
  startsAt: string;
  endsAt: string;
  internalNote: string | null;
  clientNote: string | null;
  totalPriceCents: number;
  version: number;
  canEdit: boolean;
  customer: {
    id: string;
    name: string;
    firstName: string;
    lastName: string | null;
    phone: string | null;
    email: string | null;
    alertNote: string | null;
    visits: number;
    noShows: number;
  } | null;
  items: Array<{ id: string; serviceId: string; variantId: string | null; name: string; staffMemberId: string; staffRequested: boolean; startsAt: string; endsAt: string; priceCents: number }>;
  events: Array<{ type: string; at: string; actorName: string | null; data: Record<string, unknown> | null }>;
};

export type CustomerSummary = { id: string; firstName: string; lastName: string | null; phone: string | null; email: string | null; alertNote: string | null };

export type CatalogService = {
  id: string;
  name: string;
  priceType: 'fixed' | 'from';
  priceCents: number;
  clientMinutes: number;
  staff: 'all' | string[];
  variants: Array<{ id: string; name: string; priceCents: number; clientMinutes: number }>;
};
export type CatalogCategory = { id: string; name: string; services: CatalogService[] };

export const calendarKey = (orgId: string) => ['calendar', orgId] as const;

export function useCalendar(orgId: string, locationId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: [...calendarKey(orgId), locationId, from, to],
    queryFn: () => api<CalendarData>(`/orgs/${orgId}/calendar?${new URLSearchParams({ locationId: locationId!, from, to })}`),
    enabled: !!locationId,
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });
}

export function useAppointment(orgId: string, id: string | null) {
  return useQuery({
    queryKey: ['appointment', orgId, id],
    queryFn: () => api<{ appointment: AppointmentDetail }>(`/orgs/${orgId}/appointments/${id}`).then((r) => r.appointment),
    enabled: !!id,
  });
}

export function useCatalog(orgId: string) {
  return useQuery({ queryKey: ['catalog', orgId], queryFn: () => api<{ categories: CatalogCategory[] }>(`/orgs/${orgId}/catalog`), select: (d) => d.categories });
}

export function refreshCalendar(qc: QueryClient, orgId: string, appointmentId?: string) {
  void qc.invalidateQueries({ queryKey: calendarKey(orgId) });
  if (appointmentId) void qc.invalidateQueries({ queryKey: ['appointment', orgId, appointmentId] });
}

/**
 * Live updates: a Server-Sent Events stream tells the calendar when anything changes
 * (an online booking, a colleague moving an appointment). The server sends a heartbeat every
 * 25 seconds; if nothing arrives for 70 seconds (a sleeping tablet, a dropped network) the app
 * reconnects and refetches, so the calendar never silently goes stale.
 */
export function useLiveCalendar(orgId: string) {
  const qc = useQueryClient();
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    let source: EventSource | null = null;
    let lastSeen = Date.now();
    const seen = () => {
      lastSeen = Date.now();
    };
    const connect = () => {
      source?.close();
      source = new EventSource(`/api/v1/orgs/${orgId}/stream`, { withCredentials: true });
      source.addEventListener('ping', seen);
      source.addEventListener('change', (e) => {
        seen();
        try {
          const data = JSON.parse((e as MessageEvent<string>).data) as { appointmentId?: string };
          refreshCalendar(qc, orgId, data.appointmentId);
        } catch {
          refreshCalendar(qc, orgId);
        }
      });
      // After a (re)connect, catch up on anything missed.
      source.addEventListener('open', () => {
        seen();
        refreshCalendar(qc, orgId);
      });
    };
    connect();
    const watchdog = window.setInterval(() => {
      if (document.visibilityState === 'visible' && Date.now() - lastSeen > 70_000) {
        seen();
        connect();
      }
    }, 15_000);
    return () => {
      window.clearInterval(watchdog);
      source?.close();
    };
  }, [orgId, qc]);
}

/** Applies a move to the cached calendar right away, so dragging feels instant. */
export function moveInCache(qc: QueryClient, orgId: string, id: string, change: { startsAt: string; durationMs?: number; staffMemberId?: string }) {
  const snapshots = qc.getQueriesData<CalendarData>({ queryKey: calendarKey(orgId) });
  for (const [key, data] of snapshots) {
    if (!data) continue;
    qc.setQueryData<CalendarData>(key, {
      ...data,
      appointments: data.appointments.map((a) => {
        if (a.id !== id) return a;
        const delta = Date.parse(change.startsAt) - Date.parse(a.startsAt);
        const oldDuration = Date.parse(a.endsAt) - Date.parse(a.startsAt);
        const grow = change.durationMs !== undefined ? change.durationMs - oldDuration : 0;
        const shift = (iso: string, extra = 0) => new Date(Date.parse(iso) + delta + extra).toISOString();
        const lastActive = a.parts.map((p) => p.kind).lastIndexOf('active');
        return {
          ...a,
          startsAt: change.startsAt,
          endsAt: shift(a.endsAt, grow),
          staffIds: change.staffMemberId ? [change.staffMemberId] : a.staffIds,
          parts: a.parts.map((p, i) => ({
            ...p,
            staffMemberId: change.staffMemberId ?? p.staffMemberId,
            start: shift(p.start, i > lastActive ? grow : 0),
            end: shift(p.end, i >= lastActive ? grow : 0),
          })),
        };
      }),
    });
  }
  return () => snapshots.forEach(([key, data]) => qc.setQueryData(key, data));
}
